import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { 
  Heart, Check, ArrowRight, Loader2, Gift, Users, Building, BookOpen,
  CreditCard, Smartphone, Eye, TrendingUp, TrendingDown, FileCheck,
  Wallet, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DonatePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [donorName, setDonorName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTransparency, setShowTransparency] = useState(false);
  const [showMobileMoneyModal, setShowMobileMoneyModal] = useState(false);
  const [mobileMoneyInstructions, setMobileMoneyInstructions] = useState(null);

  const t = {
    fr: {
      title: "Faire un don",
      subtitle: "Soutenez l'Alliance Démocratique Africaine",
      description: "Chaque contribution, même modeste, nous aide à construire une Côte d'Ivoire plus juste. Transparence totale : chaque franc est tracé.",
      amount: "Montant du don",
      minAmount: "Minimum 500 FCFA",
      paymentMethod: "Mode de paiement",
      card: "Carte bancaire",
      mobileMoney: "Mobile Money",
      phoneNumber: "Numéro de téléphone",
      phonePlaceholder: "+225 XX XX XX XX XX",
      yourName: "Votre nom (optionnel)",
      anonymous: "Don anonyme",
      donate: "Faire un don",
      processing: "Traitement en cours...",
      transparency: "Transparence",
      viewTransparency: "Voir la transparence des comptes",
      totalCollected: "Total collecté",
      totalDonors: "Donateurs",
      totalExpenses: "Dépenses",
      available: "Solde disponible",
      auditedBy: "Comptes audités en temps réel par",
      recentDonations: "Derniers dons",
      recentExpenses: "Dernières dépenses",
      suggestedAmounts: "Montants suggérés",
      impact: "Impact de votre don",
      impacts: [
        { amount: "500 FCFA", desc: "1 affiche de campagne" },
        { amount: "2 000 FCFA", desc: "10 tracts distribués" },
        { amount: "5 000 FCFA", desc: "Participation réunion locale" },
        { amount: "10 000 FCFA", desc: "Soutien événement terrain" },
      ],
      secure: "Paiement 100% sécurisé",
      providers: {
        orange: "Orange Money",
        mtn: "MTN MoMo",
        wave: "Wave",
        moov: "Moov Money"
      }
    },
    en: {
      title: "Make a Donation",
      subtitle: "Support the African Democratic Alliance",
      description: "Every contribution, even modest, helps us build a fairer Côte d'Ivoire. Full transparency: every franc is tracked.",
      amount: "Donation amount",
      minAmount: "Minimum 500 FCFA",
      paymentMethod: "Payment method",
      card: "Credit/Debit Card",
      mobileMoney: "Mobile Money",
      phoneNumber: "Phone number",
      phonePlaceholder: "+225 XX XX XX XX XX",
      yourName: "Your name (optional)",
      anonymous: "Anonymous donation",
      donate: "Donate",
      processing: "Processing...",
      transparency: "Transparency",
      viewTransparency: "View account transparency",
      totalCollected: "Total collected",
      totalDonors: "Donors",
      totalExpenses: "Expenses",
      available: "Available balance",
      auditedBy: "Accounts audited in real-time by",
      recentDonations: "Recent donations",
      recentExpenses: "Recent expenses",
      suggestedAmounts: "Suggested amounts",
      impact: "Impact of your donation",
      impacts: [
        { amount: "500 FCFA", desc: "1 campaign poster" },
        { amount: "2,000 FCFA", desc: "10 distributed flyers" },
        { amount: "5,000 FCFA", desc: "Local meeting participation" },
        { amount: "10,000 FCFA", desc: "Field event support" },
      ],
      secure: "100% secure payment",
      providers: {
        orange: "Orange Money",
        mtn: "MTN MoMo",
        wave: "Wave",
        moov: "Moov Money"
      }
    },
    pt: {
      title: "Fazer uma Doação",
      subtitle: "Apoie a Aliança Democrática Africana",
      description: "Cada contribuição, mesmo modesta, ajuda-nos a construir uma Costa do Marfim mais justa. Transparência total: cada franco é rastreado.",
      amount: "Montante da doação",
      minAmount: "Mínimo 500 FCFA",
      paymentMethod: "Método de pagamento",
      card: "Cartão",
      mobileMoney: "Mobile Money",
      phoneNumber: "Número de telefone",
      phonePlaceholder: "+225 XX XX XX XX XX",
      yourName: "O seu nome (opcional)",
      anonymous: "Doação anónima",
      donate: "Doar",
      processing: "A processar...",
      transparency: "Transparência",
      viewTransparency: "Ver transparência das contas",
      totalCollected: "Total recolhido",
      totalDonors: "Doadores",
      totalExpenses: "Despesas",
      available: "Saldo disponível",
      auditedBy: "Contas auditadas em tempo real por",
      recentDonations: "Doações recentes",
      recentExpenses: "Despesas recentes",
      suggestedAmounts: "Montantes sugeridos",
      impact: "Impacto da sua doação",
      impacts: [
        { amount: "500 FCFA", desc: "1 cartaz de campanha" },
        { amount: "2.000 FCFA", desc: "10 folhetos distribuídos" },
        { amount: "5.000 FCFA", desc: "Participação em reunião local" },
        { amount: "10.000 FCFA", desc: "Apoio a evento de campo" },
      ],
      secure: "Pagamento 100% seguro",
      providers: {
        orange: "Orange Money",
        mtn: "MTN MoMo",
        wave: "Wave",
        moov: "Moov Money"
      }
    }
  };

  const content = t[language] || t.fr;

  const suggestedAmounts = [500, 1000, 2000, 5000, 10000, 25000, 50000];

  const mobileProviders = [
    { id: 'orange', name: content.providers.orange, color: '#FF6600', logo: '🟠' },
    { id: 'mtn', name: content.providers.mtn, color: '#FFCC00', logo: '🟡' },
    { id: 'wave', name: content.providers.wave, color: '#1DC8F2', logo: '🔵' },
    { id: 'moov', name: content.providers.moov, color: '#0066B3', logo: '🔷' },
  ];

  const [selectedProvider, setSelectedProvider] = useState('orange');

  // Mock transparency data (would come from backend in production)
  const transparencyData = {
    totalCollected: 2847500,
    totalDonors: 156,
    totalExpenses: 1245000,
    available: 1602500,
    lastUpdated: new Date().toISOString(),
    auditor: "Cabinet KPMG Côte d'Ivoire",
    recentDonations: [
      { date: '2025-01-27', amount: 25000, donor: 'K. Kouassi', anonymous: false },
      { date: '2025-01-27', amount: 5000, donor: 'Anonyme', anonymous: true },
      { date: '2025-01-26', amount: 50000, donor: 'M. Traoré', anonymous: false },
      { date: '2025-01-26', amount: 10000, donor: 'Anonyme', anonymous: true },
      { date: '2025-01-25', amount: 2000, donor: 'A. Koné', anonymous: false },
    ],
    recentExpenses: [
      { date: '2025-01-26', amount: 150000, category: 'Impression tracts', ref: 'DEP-2025-042' },
      { date: '2025-01-24', amount: 75000, category: 'Location salle Bouaké', ref: 'DEP-2025-041' },
      { date: '2025-01-22', amount: 200000, category: 'Transport équipe terrain', ref: 'DEP-2025-040' },
      { date: '2025-01-20', amount: 50000, category: 'Fournitures bureau', ref: 'DEP-2025-039' },
    ]
  };

  const formatCFA = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const handleDonate = async () => {
    const numAmount = parseInt(amount);
    if (isNaN(numAmount) || numAmount < 500) {
      setError(content.minAmount);
      return;
    }

    if (paymentMethod === 'mobile' && !phoneNumber) {
      setError(content.phonePlaceholder);
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      if (paymentMethod === 'card') {
        // Call Stripe checkout API
        const response = await fetch(`${API_URL}/api/donations/checkout/custom`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: numAmount,
            origin_url: window.location.origin,
            donor_name: isAnonymous ? null : donorName || null,
            donor_email: null,
            message: null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Erreur de paiement');
        }

        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        // Mobile Money - call API to initiate payment
        const response = await fetch(`${API_URL}/api/donations/mobile-money/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: numAmount,
            phone_number: phoneNumber,
            provider: selectedProvider,
            donor_name: isAnonymous ? null : donorName || null,
            donor_email: null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Erreur Mobile Money');
        }

        const data = await response.json();
        
        // If FedaPay payment URL is available, redirect to it
        if (data.payment_url && data.fedapay_enabled) {
          window.location.href = data.payment_url;
        } else {
          // Otherwise show instructions modal
          setMobileMoneyInstructions(data);
          setShowMobileMoneyModal(true);
          setIsLoading(false);
        }
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
      {/* Hero */}
      <section className="py-12 bg-gradient-to-br from-[#5B2D8E] to-[#7B4AAE] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#0D9488]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#F7C948]/20 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 mb-4"
            >
              <Heart className="w-5 h-5 text-[#F7C948]" />
              <span className="text-sm font-medium">Côte d'Ivoire Solidaire</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl font-bold mb-3"
            >
              {content.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-white/80 mb-4"
            >
              {content.subtitle}
            </motion.p>
            
            {/* Transparency Toggle */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              onClick={() => setShowTransparency(!showTransparency)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0D9488] text-white text-sm font-medium hover:bg-[#0b7c72] transition-colors"
            >
              <Eye className="w-4 h-4" />
              {content.viewTransparency}
            </motion.button>
          </div>
        </div>
      </section>

      {/* Transparency Dashboard */}
      {showTransparency && (
        <motion.section
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className={`py-8 ${isDark ? 'bg-[#0D9488]/10' : 'bg-[#0D9488]/5'} border-b ${isDark ? 'border-white/10' : 'border-[#0D9488]/20'}`}
        >
          <div className="container mx-auto px-4 lg:px-10">
            <div className="max-w-5xl mx-auto">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-[#0D9488]" />
                    <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.totalCollected}</span>
                  </div>
                  <p className="text-xl font-bold text-[#0D9488]">{formatCFA(transparencyData.totalCollected)}</p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-[#5B2D8E]" />
                    <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.totalDonors}</span>
                  </div>
                  <p className="text-xl font-bold text-[#5B2D8E]">{transparencyData.totalDonors}</p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="w-4 h-4 text-[#F7C948]" />
                    <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.totalExpenses}</span>
                  </div>
                  <p className="text-xl font-bold text-[#F7C948]">{formatCFA(transparencyData.totalExpenses)}</p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Wallet className="w-4 h-4 text-[#0D9488]" />
                    <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.available}</span>
                  </div>
                  <p className="text-xl font-bold text-[#0D9488]">{formatCFA(transparencyData.available)}</p>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {/* Recent Donations */}
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                    <TrendingUp className="w-4 h-4 text-[#0D9488]" />
                    {content.recentDonations}
                  </h3>
                  <div className="space-y-2">
                    {transparencyData.recentDonations.map((donation, i) => (
                      <div key={i} className={`flex justify-between items-center py-2 border-b last:border-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                        <div>
                          <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            {donation.anonymous ? '👤 Anonyme' : donation.donor}
                          </span>
                          <span className={`text-xs block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{donation.date}</span>
                        </div>
                        <span className="text-sm font-bold text-[#0D9488]">+{formatCFA(donation.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Expenses */}
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}>
                  <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                    <TrendingDown className="w-4 h-4 text-[#F7C948]" />
                    {content.recentExpenses}
                  </h3>
                  <div className="space-y-2">
                    {transparencyData.recentExpenses.map((expense, i) => (
                      <div key={i} className={`flex justify-between items-center py-2 border-b last:border-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                        <div>
                          <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>{expense.category}</span>
                          <span className={`text-xs block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{expense.ref} • {expense.date}</span>
                        </div>
                        <span className="text-sm font-bold text-[#F7C948]">-{formatCFA(expense.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Auditor Badge */}
              <div className={`flex items-center justify-center gap-3 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-[#0D9488]/10'}`}>
                <FileCheck className="w-5 h-5 text-[#0D9488]" />
                <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {content.auditedBy} <strong>{transparencyData.auditor}</strong>
                </span>
                <RefreshCw className={`w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto grid lg:grid-cols-5 gap-8">
            
            {/* Donation Form - Takes 3 columns */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`lg:col-span-3 p-6 rounded-2xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-xl`}
            >
              {/* Amount Input */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {content.amount}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="500"
                    step="100"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="500"
                    className={`w-full px-4 py-4 pr-20 rounded-xl text-2xl font-bold ${
                      isDark 
                        ? 'bg-white/10 text-white placeholder-white/30' 
                        : 'bg-gray-100 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                  <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-lg font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                    FCFA
                  </span>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {content.minAmount}
                </p>
              </div>

              {/* Suggested Amounts */}
              <div className="mb-6">
                <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {content.suggestedAmounts}
                </label>
                <div className="flex flex-wrap gap-2">
                  {suggestedAmounts.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setAmount(amt.toString())}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        parseInt(amount) === amt
                          ? 'bg-[#0D9488] text-white'
                          : isDark
                            ? 'bg-white/10 text-white hover:bg-white/20'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {new Intl.NumberFormat('fr-FR').format(amt)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {content.paymentMethod}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`p-4 rounded-xl flex items-center gap-3 transition-all border-2 ${
                      paymentMethod === 'card'
                        ? 'border-[#5B2D8E] bg-[#5B2D8E]/10'
                        : isDark
                          ? 'border-white/10 hover:border-white/30'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className={`w-6 h-6 ${paymentMethod === 'card' ? 'text-[#5B2D8E]' : isDark ? 'text-white/50' : 'text-gray-400'}`} />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{content.card}</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('mobile')}
                    className={`p-4 rounded-xl flex items-center gap-3 transition-all border-2 ${
                      paymentMethod === 'mobile'
                        ? 'border-[#0D9488] bg-[#0D9488]/10'
                        : isDark
                          ? 'border-white/10 hover:border-white/30'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className={`w-6 h-6 ${paymentMethod === 'mobile' ? 'text-[#0D9488]' : isDark ? 'text-white/50' : 'text-gray-400'}`} />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{content.mobileMoney}</span>
                  </button>
                </div>
              </div>

              {/* Mobile Money Providers */}
              {paymentMethod === 'mobile' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-6"
                >
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {mobileProviders.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => setSelectedProvider(provider.id)}
                        className={`p-3 rounded-xl text-center transition-all border-2 ${
                          selectedProvider === provider.id
                            ? 'border-[#0D9488]'
                            : isDark
                              ? 'border-white/10'
                              : 'border-gray-200'
                        }`}
                        style={selectedProvider === provider.id ? { backgroundColor: `${provider.color}15` } : {}}
                      >
                        <span className="text-2xl block mb-1">{provider.logo}</span>
                        <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                          {provider.name.split(' ')[0]}
                        </span>
                      </button>
                    ))}
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={content.phonePlaceholder}
                    className={`w-full px-4 py-3 rounded-xl ${
                      isDark 
                        ? 'bg-white/10 text-white placeholder-white/40' 
                        : 'bg-gray-100 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </motion.div>
              )}

              {/* Donor Name */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {content.yourName}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#5B2D8E] focus:ring-[#5B2D8E]"
                    />
                    <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.anonymous}</span>
                  </label>
                </div>
                {!isAnonymous && (
                  <input
                    type="text"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="Votre nom"
                    className={`w-full px-4 py-3 rounded-xl ${
                      isDark 
                        ? 'bg-white/10 text-white placeholder-white/40' 
                        : 'bg-gray-100 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleDonate}
                disabled={isLoading || !amount || parseInt(amount) < 500}
                className="w-full py-4 px-6 rounded-xl bg-[#F7C948] text-[#1A0F2E] font-bold text-lg flex items-center justify-center gap-3 hover:bg-[#FFD966] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="donate-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {content.processing}
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5" />
                    {content.donate}
                    {amount && parseInt(amount) >= 500 && (
                      <span>— {formatCFA(parseInt(amount))}</span>
                    )}
                  </>
                )}
              </button>

              {/* Security Note */}
              <p className={`text-center text-xs mt-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                🔒 {content.secure}
              </p>
            </motion.div>

            {/* Impact Section - Takes 2 columns */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                {content.impact}
              </h2>

              <div className="space-y-3">
                {content.impacts.map((impact, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      index % 3 === 0 ? 'bg-[#5B2D8E]/10' : index % 3 === 1 ? 'bg-[#F7C948]/10' : 'bg-[#0D9488]/10'
                    }`}>
                      <Gift className={`w-5 h-5 ${
                        index % 3 === 0 ? 'text-[#5B2D8E]' : index % 3 === 1 ? 'text-[#F7C948]' : 'text-[#0D9488]'
                      }`} />
                    </div>
                    <div>
                      <span className="text-[#0D9488] font-bold text-sm">{impact.amount}</span>
                      <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{impact.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {content.description}
                </p>
              </div>

              {/* Link to full transparency */}
              <Link
                to="/transparence"
                className={`mt-4 flex items-center justify-center gap-2 p-3 rounded-xl ${
                  isDark ? 'bg-[#0D9488]/20 text-[#0D9488]' : 'bg-[#0D9488]/10 text-[#0D9488]'
                } font-medium text-sm hover:bg-[#0D9488]/30 transition-colors`}
              >
                <Eye className="w-4 h-4" />
                {content.viewTransparency}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile Money Instructions Modal */}
      {showMobileMoneyModal && mobileMoneyInstructions && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowMobileMoneyModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-xl overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-2xl`}
          >
            {/* Header */}
            <div 
              className="p-6 text-white text-center"
              style={{ backgroundColor: mobileMoneyInstructions.provider?.color || '#5B2D8E' }}
            >
              <Smartphone className="w-12 h-12 mx-auto mb-3 opacity-80" />
              <h3 className="text-xl font-bold mb-1">
                {mobileMoneyInstructions.provider?.name || 'Mobile Money'}
              </h3>
              <p className="text-3xl font-black">
                {formatCFA(mobileMoneyInstructions.amount)}
              </p>
            </div>

            {/* Instructions */}
            <div className="p-6">
              <div className={`mb-4 p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Référence de transaction :
                </p>
                <p className={`text-lg font-bold font-mono ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>
                  {mobileMoneyInstructions.reference}
                </p>
              </div>

              <div className={`mb-6 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                <p className={`text-sm font-medium mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  Instructions :
                </p>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                  {mobileMoneyInstructions.instructions}
                </pre>
              </div>

              <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-orange-200 bg-orange-50'}`}>
                <p className={`text-sm ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                  <strong>Important :</strong> N'oubliez pas d'inclure la référence <strong>{mobileMoneyInstructions.reference}</strong> dans votre transaction.
                </p>
              </div>

              <button
                onClick={() => setShowMobileMoneyModal(false)}
                className="w-full mt-6 py-3 bg-[#F7C948] text-[#0A0510] font-bold rounded-lg hover:bg-[#E5A700] transition-colors"
              >
                J'ai effectué le paiement
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DonatePage;
